import React, { useMemo } from 'react';
import { Box, Typography, Tooltip as MuiTooltip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import {
  AgingBucket,
  PayableExpenseLike,
  daysUntilDue,
  expenseAgingBucket,
  expenseOverlapsWindow,
} from '../../utils/payablesFilters';
import {
  chartColors,
  gridProps,
  axisTick,
  quietAxisProps,
  tooltipContentStyle as sharedTooltipContentStyle,
  barRadius,
  MAX_BAR_SIZE,
  SERIES_FILL_OPACITY,
  activeBarProps,
  legendProps,
} from './chartTheme';

interface SupplierLike {
  supplierName: string;
  expenses: PayableExpenseLike[];
}

interface Props {
  suppliers: SupplierLike[];
  periodWindow: { from: Date; to: Date } | null;
  onAgingClick?: (bucket: AgingBucket) => void;
  onSupplierClick?: (name: string) => void;
}

const AGING_LABELS: Record<AgingBucket, string> = {
  current: 'Current',
  '1_30': '1–30d',
  '31_60': '31–60d',
  '60_plus': '60+d',
};
const AGING_ORDER: AgingBucket[] = ['current', '1_30', '31_60', '60_plus'];

const PayablesCharts: React.FC<Props> = ({ suppliers, periodWindow, onAgingClick, onSupplierClick }) => {
  const { theme } = useTheme();

  const agingColor = (b: AgingBucket): string => {
    if (b === 'current') return theme.success || '#4CAF50';
    if (b === '1_30') return theme.warning || '#FFC107';
    if (b === '31_60') return '#FF9800';
    return theme.danger || '#D32F2F';
  };

  // --- Aging breakdown ---
  const agingData = useMemo(() => {
    const today = new Date();
    const totals: Record<AgingBucket, number> = { current: 0, '1_30': 0, '31_60': 0, '60_plus': 0 };
    suppliers.forEach((s) => {
      s.expenses.forEach((e) => {
        const owed = Math.max((e.amount || 0) - (e.amountPaid || 0), 0);
        if (owed <= 0) return;
        totals[expenseAgingBucket(e, today)] += owed;
      });
    });
    const total = AGING_ORDER.reduce((sum, b) => sum + totals[b], 0);
    return { totals, total };
  }, [suppliers]);

  // --- Top suppliers paid vs outstanding (period-aware) ---
  const supplierBarData = useMemo(() => {
    const rows = suppliers.map((s) => {
      let paid = 0;
      let outstanding = 0;
      s.expenses.forEach((e) => {
        if (periodWindow && !expenseOverlapsWindow(e, periodWindow.from, periodWindow.to)) return;
        paid += e.amountPaid || 0;
        outstanding += Math.max((e.amount || 0) - (e.amountPaid || 0), 0);
      });
      return { name: s.supplierName, paid, outstanding, total: paid + outstanding };
    });
    return rows
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [suppliers, periodWindow]);

  // --- 30/60/90 forecast (outstanding only) ---
  const forecastData = useMemo(() => {
    const today = new Date();
    const buckets = { overdue: 0, d30: 0, d60: 0, d90: 0 };
    suppliers.forEach((s) => {
      s.expenses.forEach((e) => {
        const owed = Math.max((e.amount || 0) - (e.amountPaid || 0), 0);
        if (owed <= 0) return;
        const days = daysUntilDue(e, today);
        if (days === null) return;
        if (days < 0) buckets.overdue += owed;
        else if (days <= 30) buckets.d30 += owed;
        else if (days <= 60) buckets.d60 += owed;
        else if (days <= 90) buckets.d90 += owed;
      });
    });
    return [
      { name: 'Overdue', amount: buckets.overdue, fill: chartColors.expense },
      { name: '≤30d', amount: buckets.d30, fill: theme.warning || chartColors.neutral },
      { name: '31–60d', amount: buckets.d60, fill: chartColors.neutral },
      { name: '61–90d', amount: buckets.d90, fill: chartColors.income },
    ];
  }, [suppliers, theme]);

  const cardSx = {
    backgroundColor: theme.components?.card?.background || '#FFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
    borderRadius: '12px',
    p: { xs: 1.5, sm: 2 },
  };

  const tickStyle = axisTick;
  const tooltipContentStyle = sharedTooltipContentStyle;

  // --- Render ---
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* Aging stacked bar */}
      <Box sx={cardSx}>
        <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
          Aging breakdown · click a segment to filter
        </Typography>
        {agingData.total === 0 ? (
          <Typography sx={{ color: theme.gray?.[400], fontSize: '0.8rem', textAlign: 'center', py: 1.5 }}>
            No outstanding amount
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', mt: 1, height: 28, borderRadius: '8px', overflow: 'hidden', boxShadow: `inset 0 0 0 1px ${theme.gray?.[200] || '#E5E5E5'}` }}>
            {AGING_ORDER.map((bucket) => {
              const value = agingData.totals[bucket];
              if (value <= 0) return null;
              const pct = (value / agingData.total) * 100;
              return (
                <MuiTooltip
                  key={bucket}
                  title={`${AGING_LABELS[bucket]}: ${formatCurrency(value, { showDecimals: false })} (${pct.toFixed(1)}%)`}
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={() => onAgingClick?.(bucket)}
                    role="button"
                    aria-label={`Filter by ${AGING_LABELS[bucket]}`}
                    data-testid={`aging-segment-${bucket}`}
                    sx={{
                      flex: pct,
                      backgroundColor: agingColor(bucket),
                      cursor: onAgingClick ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-inverse)',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      transition: 'opacity 0.15s',
                      '&:hover': onAgingClick ? { opacity: 0.85 } : undefined,
                    }}
                  >
                    {pct >= 8 ? AGING_LABELS[bucket] : ''}
                  </Box>
                </MuiTooltip>
              );
            })}
          </Box>
        )}
        {agingData.total > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
            {AGING_ORDER.map((bucket) => (
              <Box key={bucket} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: agingColor(bucket) }} />
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.65rem' }}>
                  {AGING_LABELS[bucket]}: {formatCurrency(agingData.totals[bucket], { showDecimals: false })}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Supplier paid-vs-outstanding + 30/60/90 forecast side-by-side */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' } }}>
        <Box sx={cardSx}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            Top suppliers — Paid vs Outstanding {periodWindow ? '(in period)' : '(lifetime)'}
          </Typography>
          {supplierBarData.length === 0 ? (
            <Typography sx={{ color: theme.gray?.[400], fontSize: '0.8rem', textAlign: 'center', py: 4 }}>
              No data in current view
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, supplierBarData.length * 24 + 60)}>
              <BarChart data={supplierBarData} margin={{ top: 12, right: 16, left: 0, bottom: 16 }}>
                <CartesianGrid {...gridProps} />
                <XAxis
                  dataKey="name"
                  tick={{ ...tickStyle, cursor: onSupplierClick ? 'pointer' : 'default' }}
                  tickFormatter={(v: string) => (v.length > 12 ? `${v.slice(0, 11)}…` : v)}
                  interval={0}
                  angle={-20}
                  height={50}
                  textAnchor="end"
                  onClick={(o: { value?: string }) => o?.value && onSupplierClick?.(o.value)}
                  {...quietAxisProps}
                />
                <YAxis
                  tick={tickStyle}
                  tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
                  {...quietAxisProps}
                />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value, { showDecimals: false })}
                  contentStyle={tooltipContentStyle}
                />
                <Legend {...legendProps} />
                <Bar
                  dataKey="paid"
                  name="Paid"
                  stackId="a"
                  fill={chartColors.income}
                  fillOpacity={SERIES_FILL_OPACITY}
                  maxBarSize={MAX_BAR_SIZE}
                  cursor={onSupplierClick ? 'pointer' : 'default'}
                  onClick={(p: { name?: string }) => p?.name && onSupplierClick?.(p.name)}
                  {...activeBarProps}
                />
                <Bar
                  dataKey="outstanding"
                  name="Outstanding"
                  stackId="a"
                  fill={chartColors.expense}
                  fillOpacity={SERIES_FILL_OPACITY}
                  radius={barRadius}
                  maxBarSize={MAX_BAR_SIZE}
                  cursor={onSupplierClick ? 'pointer' : 'default'}
                  onClick={(p: { name?: string }) => p?.name && onSupplierClick?.(p.name)}
                  {...activeBarProps}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            Cash needed — by due window
          </Typography>
          {forecastData.every((d) => d.amount === 0) ? (
            <Typography sx={{ color: theme.gray?.[400], fontSize: '0.8rem', textAlign: 'center', py: 4 }}>
              No outstanding with due dates
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={forecastData} margin={{ top: 12, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" tick={tickStyle} {...quietAxisProps} />
                <YAxis
                  tick={tickStyle}
                  tickFormatter={(v: number) => formatCurrency(v, { showDecimals: false, compact: true })}
                  {...quietAxisProps}
                />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value, { showDecimals: false })}
                  contentStyle={tooltipContentStyle}
                />
                <Bar
                  dataKey="amount"
                  name="Outstanding"
                  radius={barRadius}
                  fillOpacity={SERIES_FILL_OPACITY}
                  maxBarSize={MAX_BAR_SIZE}
                  {...activeBarProps}
                >
                  {forecastData.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                  <LabelList
                    dataKey="amount"
                    position="top"
                    formatter={(v: number) => (v > 0 ? formatCurrency(v, { showDecimals: false, compact: true }) : '')}
                    style={{ fontSize: 10, fill: theme.text }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PayablesCharts;
